import { MessageCollection } from '@sendbird/chat/groupChannel';
import { FileMessage } from '@sendbird/chat/message';
import { infiniteQueryOptions } from '@tanstack/react-query';
import { type EmblaCarouselType } from 'embla-carousel';
import { fileMessagesToSlides } from './fileMessagesToSlides';
import { getAllFileMessages } from './getAllFileMessages';

type Params = {
  channelUrl: string;
  cachedMessagesLength: number;
  messageCollection: MessageCollection;
  selectedIndex: number;
  emblaApi?: EmblaCarouselType;
  startSlideUrl?: string;
};

export const useMediaMessagesQuery = ({
  channelUrl,
  cachedMessagesLength,
  messageCollection,
  selectedIndex,
  emblaApi,
  startSlideUrl,
}: Params) =>
  infiniteQueryOptions({
    queryKey: [
      'mediaMessages',
      {
        channelUrl,
        cachedMessagesLength,
        startSlideUrl,
      },
    ],
    staleTime: Infinity,
    initialData: () => ({
      pages: [fileMessagesToSlides(getAllFileMessages(messageCollection))],
      pageParams: [null],
    }),
    initialPageParam: null as 'NEXT' | 'PREVIOUS' | null,
    getNextPageParam: () => (messageCollection.hasNext ? 'NEXT' : undefined),
    getPreviousPageParam: () =>
      messageCollection.hasPrevious ? 'PREVIOUS' : undefined,

    queryFn: async ({ pageParam }) => {
      let data: FileMessage[] = [];
      if (pageParam === 'NEXT') {
        data = (await messageCollection.loadNext()) as FileMessage[];
      } else {
        data = (await messageCollection.loadPrevious()) as FileMessage[];

        const prevIndex = selectedIndex;

        // If there are no more previous messages, we need to adjust the index to account for the loading indicator being removed
        const currentOffset = getMediaStartIndex({ hasPrevious: true });
        const newOffset = getMediaStartIndex(messageCollection);
        const offsetDiff = currentOffset - newOffset;

        // This is because the carousel does not automatically adjust the scroll position when new slides are added to the start
        const newIndex = prevIndex + data.length - offsetDiff;
        setTimeout(() => {
          if (emblaApi) {
            // Causes a UI flicker if the user is in the middle of switching slides, but we need to scroll to the correct position after loading previous messages :(
            emblaApi.scrollTo(newIndex, false);
          }
        }, 0);
      }

      return fileMessagesToSlides(data).reverse();
    },
  });

export function getMediaStartIndex(options: { hasPrevious: boolean }) {
  // Note: the 1st slide can be a loading indicator, so we need to adjust the index accordingly
  return options.hasPrevious ? 1 : 0;
}
